import { BuildingIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import {
  DropdownHeader,
  DropdownMenu,
  type DropdownProps,
} from "simplydo/react/ui/dropdown";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";

import StyledDropdownItem from "components/lib/UI/StyledDropdownItem";

const BusinessProfileDropdownItem = ({
  businessProfile,
}: {
  businessProfile: OpenAPI.Schemas["IdeaBusinessProfile"];
}) => {
  const { name, imageUrl, address = {}, _id } = businessProfile;
  const { country, city } = address;
  return (
    <StyledDropdownItem
      key={_id}
      text={name ?? ""}
      value={_id}
      image={
        imageUrl ? (
          <Image
            src={imageUrl}
            style={{
              borderRadius: 2,
              overflow: "hidden",
              height: 28,
              width: 28,
              objectFit: "contain",
            }}
          />
        ) : (
          <Icon icon={BuildingIcon} size="large" />
        )
      }
      description={
        city || country
          ? `${city || ""}${city && country ? ", " : ""}${country}`
          : undefined
      }
    />
  );
};

type IProfileSelector = {
  selectedBusinessProfile?: OpenAPI.Schemas["IdeaBusinessProfile"];
  isLoading: boolean;
  existingProfiles: OpenAPI.Schemas["IdeaBusinessProfile"][];
  joinableProfiles?: OpenAPI.Schemas["IdeaBusinessProfile"][];
  joinProfile?: (profileId: string) => void;
  selectBusinessProfile: (
    ideaBusinessProfile: OpenAPI.Schemas["IdeaBusinessProfile"],
  ) => void;
  deselectBusinessProfile: () => void;
};

const BusinessProfileSelector = ({
  selectedBusinessProfile,
  isLoading,
  existingProfiles,
  joinableProfiles = [],
  joinProfile,
  selectBusinessProfile,
  deselectBusinessProfile,
}: IProfileSelector) => {
  const handleChange = (_e: React.SyntheticEvent, { value }: DropdownProps) => {
    if (!value) {
      deselectBusinessProfile();
      return;
    }
    const profileId = String(value);
    const joinable = joinableProfiles.find(
      (profile) => profile._id === profileId,
    );
    if (joinable) {
      joinProfile?.(profileId);
      selectBusinessProfile(joinable);
      return;
    }
    const existing = existingProfiles.find(
      (profile) => profile._id === profileId,
    );
    if (existing) {
      selectBusinessProfile(existing);
    }
  };

  return (
    <Dropdown
      variant="outline"
      fluid
      clearable
      placeholder="Select an organisation profile.."
      loading={isLoading}
      value={selectedBusinessProfile?._id}
      onChange={handleChange}
      options={[
        ...(joinableProfiles || []).map((joinableProfile) => ({
          key: joinableProfile._id,
          text: joinableProfile.name,
          value: joinableProfile._id,
        })),
        ...(existingProfiles || []).map((existingProfile) => ({
          key: existingProfile._id,
          text: existingProfile.name,
          value: existingProfile._id,
        })),
      ]}
    >
      <DropdownMenu>
        {joinableProfiles.length > 0 ? (
          <DropdownHeader>Organisations you can join</DropdownHeader>
        ) : null}
        {(joinableProfiles || []).map((joinableProfile) => (
          <BusinessProfileDropdownItem
            key={joinableProfile._id}
            businessProfile={joinableProfile}
          />
        ))}
        {existingProfiles.length > 0 ? (
          <DropdownHeader>Your organisations</DropdownHeader>
        ) : null}
        {(existingProfiles || []).map((existingProfile) => (
          <BusinessProfileDropdownItem
            key={existingProfile._id}
            businessProfile={existingProfile}
          />
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

export default BusinessProfileSelector;
